import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Gastos = () => {
  const { hasPermission } = usePermissions();
  const [gastos, setGastos] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [detallesTemporal, setDetallesTemporal] = useState([]);
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [importeTemporal, setImporteTemporal] = useState(0);
  const [fechaRango, setFechaRango] = useState([dayjs(), dayjs()]);
  const [saving, setSaving] = useState(false);

  const loadGastos = async (rangoOverride = null) => {
    setLoading(true);
    try {
      const rango = rangoOverride != null ? rangoOverride : fechaRango;
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      
      const response = await apiClient.get(`/gestion/gastos?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setGastos(data);
      } else {
        message.error('Error al cargar los gastos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los gastos');
    } finally {
      setLoading(false);
    }
  };

  const loadArticulos = async () => {
    try {
      const response = await apiClient.get('/gestion/gastos/recursos/articulos');
      if (response.ok) {
        const data = await response.json();
        setArticulos(data || []);
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
    }
  };

  const loadFormasPago = async () => {
    try {
      const response = await apiClient.get('/gestion/gastos/recursos/formas-pago');
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  useEffect(() => {
    loadGastos();
    loadArticulos();
    loadFormasPago();
  }, []);

  // Cargar gastos cuando cambia el filtro de fechas
  useEffect(() => {
    if (fechaRango && fechaRango[0] && fechaRango[1]) {
      loadGastos();
    }
  }, [fechaRango]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenModal = () => {
    form.resetFields();
    // Establecer la primera forma de pago activa como valor por defecto
    const primeraFormaPago = formasPago.length > 0 ? formasPago[0].fop_nombre : null;
    form.setFieldsValue({
      fecha: dayjs(),
      metodo_pago: primeraFormaPago,
    });
    setDetallesTemporal([]);
    setArticuloSeleccionado(null);
    setImporteTemporal(0);
    setModalVisible(true);
  };

  const handleAgregarDetalle = () => {
    if (!articuloSeleccionado) {
      message.warning('Seleccione un artículo');
      return;
    }
    const importe = typeof importeTemporal === 'number' ? importeTemporal : parseFloat(importeTemporal);
    if (!importe || importe <= 0) {
      message.warning('Ingrese un importe válido');
      return;
    }

    const articulo = articulos.find(a => a.art_id === articuloSeleccionado);
    if (!articulo) {
      message.error('Artículo no encontrado');
      return;
    }

    if (detallesTemporal.some(d => d.articulo_id === articuloSeleccionado)) {
      message.warning('Este artículo ya está en la lista');
      return;
    }

    setDetallesTemporal([
      ...detallesTemporal,
      {
        articulo_id: articuloSeleccionado,
        articulo_nombre: articulo.art_nombre,
        importe,
      },
    ]);
    setArticuloSeleccionado(null);
    setImporteTemporal(0);
  };

  const handleQuitarDetalle = (index) => {
    setDetallesTemporal(detallesTemporal.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const values = await form.validateFields();

      if (detallesTemporal.length === 0) {
        message.error('Debe agregar al menos un artículo');
        return;
      }

      const dataToSend = {
        fecha: values.fecha.format('YYYY-MM-DD'),
        metodo_pago: values.metodo_pago,
        observaciones: values.observaciones || null,
        detalles: detallesTemporal.map(d => ({
          articulo_id: d.articulo_id,
          cantidad: 1,
          precio_unitario: d.importe,
        })),
      };

      const response = await apiClient.post('/gestion/gastos', dataToSend);
      if (response.ok) {
        message.success('Gasto registrado correctamente');
        setModalVisible(false);
        loadGastos();
        loadArticulos(); // Recargar artículos por si cambió algo
      } else {
        const error = await response.json();
        const msg = error.error || error.message || 'Error al registrar el gasto';
        message.error(msg);
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el gasto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/gastos/${id}`);
      if (response.ok) {
        message.success('Gasto eliminado correctamente');
        loadGastos();
        loadArticulos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el gasto');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el gasto');
    }
  };

  const handleVerDetalle = (gasto) => {
    setGastoSeleccionado(gasto);
    setModalDetalleVisible(true);
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'gas_fecha',
      key: 'gas_fecha',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Total',
      dataIndex: 'gas_total',
      key: 'gas_total',
      render: (total) => `$${parseFloat(total).toFixed(2)}`,
    },
    {
      title: 'Método de Pago',
      dataIndex: 'gas_metodo_pago',
      key: 'gas_metodo_pago',
      render: (metodo) => (
        <Tag color={metodo === 'efectivo' ? 'green' : metodo === 'transferencia' ? 'blue' : 'orange'}>
          {metodo}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          {hasPermission('gastos', 'ver_gasto') && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalle(record)}
            >
              Ver
            </Button>
          )}
          {hasPermission('gastos', 'eliminar') && (
            <Popconfirm
              title="¿Estás seguro de eliminar este gasto?"
              onConfirm={() => handleDelete(record.gas_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div>
            <div style={{ marginBottom: 16 }}>
              <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
                Gestión de Gastos
              </h1>
              <Space wrap style={{ marginBottom: 16 }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadGastos}
                  loading={loading}
                >
                  {isMobile ? null : 'Actualizar'}
                </Button>
                {hasPermission('gastos', 'crear') && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenModal}
                  >
                    Nuevo Gasto
                  </Button>
                )}
              </Space>
              <div style={{
                padding: 12,
                backgroundColor: '#2d2d2d',
                borderRadius: 8,
                marginBottom: 16,
                borderLeft: '4px solid #ff4d4f'
              }}>
                <p style={{ margin: 0, color: '#b0b0b0', fontSize: '13px' }}>
                  <strong style={{ color: '#ffffff' }}>Nota:</strong> Los gastos solo pueden incluir artículos que <strong>no mueven stock</strong> 
                  (como servicios, gastos operativos, etc.)
                </p>
              </div>

              {/* Filtros */}
              <div style={{
                marginBottom: 16,
                padding: 16,
                backgroundColor: '#2d2d2d',
                borderRadius: 8,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 12,
                alignItems: isMobile ? 'stretch' : 'flex-end'
              }}>
                <div style={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                    Rango de fechas
                  </label>
                  <RangePicker
                    value={fechaRango}
                    onChange={(dates) => setFechaRango(dates)}
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={gastos}
              loading={loading}
              rowKey="gas_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} gastos`,
              }}
            />

        {/* Modal para crear gasto */}
        <Modal
          title="Nuevo Gasto"
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
            setDetallesTemporal([]);
            setArticuloSeleccionado(null);
            setImporteTemporal(0);
          }}
          okText="Registrar"
          cancelText="Cancelar"
          confirmLoading={saving}
          width={800}
        >
          <Form form={form} layout="vertical">
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: 'Seleccione la fecha' }]}
                style={{ flex: 1 }}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>

              <Form.Item
                name="metodo_pago"
                label="Método de Pago"
                rules={[{ required: true, message: 'Seleccione el método de pago' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Seleccione forma de pago"
                  showSearch
                  filterOption={(input, option) => {
                    const text = String(option?.children || option?.label || '').toLowerCase();
                    return text.includes((input || '').toLowerCase());
                  }}
                >
                  {formasPago.map(fp => (
                    <Select.Option key={fp.fop_id} value={fp.fop_nombre}>
                      {fp.fop_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item name="observaciones" label="Observaciones">
              <TextArea rows={2} placeholder="Observaciones (opcional)" />
            </Form.Item>

            <Divider>Detalles del Gasto</Divider>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <label style={{ display: 'block', marginBottom: 4, color: 'rgba(255,255,255,.85)', fontSize: '14px' }}>
                  Artículo
                </label>
                <Select
                  placeholder="Seleccione un artículo"
                  value={articuloSeleccionado}
                  onChange={setArticuloSeleccionado}
                  showSearch
                  style={{ width: '100%' }}
                  filterOption={(input, option) => {
                    const text = String(option?.children || option?.label || '');
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {articulos
                    .filter(art => !detallesTemporal.some(d => d.articulo_id === art.art_id))
                    .map(art => (
                      <Select.Option key={art.art_id} value={art.art_id}>
                        {art.art_nombre}
                      </Select.Option>
                    ))}
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4, color: 'rgba(255,255,255,.85)', fontSize: '14px' }}>
                  Importe
                </label>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={importeTemporal}
                  onChange={(value) => setImporteTemporal(value ?? 0)}
                  formatter={(value) => {
                    if (value == null || value === '') return '';
                    const parts = String(value).split('.');
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    return `$ ${parts.join(',')}`;
                  }}
                  parser={(value) => {
                    if (!value || value === '$' || (typeof value === 'string' && value.trim() === '')) return 0;
                    let cleaned = String(value).replace(/\$\s?/g, '').trim();
                    if (!cleaned) return 0;
                    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
                    const num = parseFloat(cleaned);
                    return isNaN(num) || num < 0 ? 0 : num;
                  }}
                  style={{ width: '100%' }}
                  placeholder="Importe"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAgregarDetalle())}
                />
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAgregarDetalle} style={{ height: '32px' }}>
                Agregar
              </Button>
            </div>

            {detallesTemporal.length > 0 && (
              <Table
                dataSource={detallesTemporal}
                rowKey={(record) => record.articulo_id}
                pagination={false}
                size="small"
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  {
                    title: 'Importe',
                    dataIndex: 'importe',
                    key: 'importe',
                    align: 'right',
                    render: (imp) => {
                    const n = parseFloat(imp);
                    const parts = n.toFixed(2).split('.');
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    return `$${parts.join(',')}`;
                  },
                  },
                  {
                    title: 'Acciones',
                    key: 'acciones',
                    width: 100,
                    render: (_, record, index) => (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleQuitarDetalle(index)}
                        size="small"
                      >
                        Quitar
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Form>
        </Modal>

        {/* Modal para ver detalle */}
        <Modal
          title="Detalle de Gasto"
          open={modalDetalleVisible}
          onCancel={() => setModalDetalleVisible(false)}
          footer={null}
          width={600}
        >
          {gastoSeleccionado && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {dayjs(gastoSeleccionado.gas_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  <Tag color={gastoSeleccionado.gas_metodo_pago === 'efectivo' ? 'green' : 'blue'}>
                    {gastoSeleccionado.gas_metodo_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  ${parseFloat(gastoSeleccionado.gas_total).toFixed(2)}
                </Descriptions.Item>
                {gastoSeleccionado.gas_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {gastoSeleccionado.gas_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  {
                    title: 'Importe',
                    dataIndex: 'gad_subtotal',
                    key: 'gad_subtotal',
                    align: 'right',
                    render: (subtotal) => {
                      const n = parseFloat(subtotal);
                      const parts = n.toFixed(2).split('.');
                      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                      return `$${parts.join(',')}`;
                    },
                  },
                ]}
                dataSource={gastoSeleccionado.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          )}
        </Modal>
        </div>
      </div>
    </MainLayout>
  );
};

export default Gastos;
